import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X, Save, Building2, Calendar, Receipt, Loader2, Info } from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';

interface Property {
    id: string;
    name: string;
}

interface FixedChargeItem {
    id: string;
    description: string;
    amount: string;
}

export const CreateBillingSchedule: React.FC = () => {
    const navigate = useNavigate();
    const { warning, success, error: toastError } = useToast();
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [allProperties, setAllProperties] = useState<Property[]>([]);

    const [scheduleName, setScheduleName] = useState("Monthly Rent & Utilities");
    const [generationDay, setGenerationDay] = useState("25");
    const [dueDay, setDueDay] = useState("5");
    const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([]);
    const [fixedCharges, setFixedCharges] = useState<FixedChargeItem[]>([]);

    useEffect(() => {
        const fetchProperties = async () => {
            try {
                const response = await apiClient.get<Property[]>("/properties");
                setAllProperties(response.data);
            } catch (error) {
                console.error("Failed to fetch properties:", error);
                toastError('Failed to load properties', 'Could not load your properties. Please try again later.');
            } finally {
                setIsLoading(false);
            }
        };
        fetchProperties();
    }, []);

    const togglePropertySelection = (propertyId: string) => {
        setSelectedPropertyIds(prev => 
            prev.includes(propertyId)
                ? prev.filter(id => id !== propertyId)
                : [...prev, propertyId]
        );
    };

    const handleChargeChange = (id: string, field: "description" | "amount", value: string) => {
        setFixedCharges(current =>
            current.map(item =>
                item.id === id ? { ...item, [field]: value } : item
            )
        );
    };

    const addCharge = () => {
        setFixedCharges([
            ...fixedCharges,
            { id: Math.random().toString(), description: "", amount: "" },
        ]);
    };

    const removeCharge = (id: string) => {
        setFixedCharges(current => current.filter(item => item.id !== id));
    };

    const handleCreateSchedule = async () => {
        if (!scheduleName || !generationDay || !dueDay || selectedPropertyIds.length === 0) {
            warning('Incomplete form', 'Please fill all fields and select at least one property.');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                scheduleName,
                generationDay: parseInt(generationDay, 10),
                dueDay: parseInt(dueDay, 10),
                propertyIds: selectedPropertyIds,
                fixedCharges: fixedCharges
                    .filter(c => c.description && c.amount)
                    .map(c => ({
                        description: c.description,
                        amount: parseFloat(c.amount),
                    })),
            };
            await apiClient.post("/billing/schedules", payload);
            success('Schedule created', 'Automated billing schedule has been created successfully.');
            navigate('/landlord/billing/schedules');
        } catch (error: any) {
            console.error(error);
            toastError('Failed to create schedule', error.response?.data?.error || 'An unexpected error occurred.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex h-[50vh] items-center justify-center">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
        );
    }

    const inputClass = "w-full px-4 py-3 bg-light-input dark:bg-dark-input border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-dark-text outline-none focus:ring-2 focus:ring-primary/20";
    const labelClass = "block text-sm font-semibold text-light-subtle dark:text-dark-subtle mb-2";

    return (
        <div className="max-w-3xl mx-auto pb-12 space-y-6">
            <div className="flex items-center gap-4 bg-light-card dark:bg-dark-card p-4 rounded-lg shadow-sm border border-light-border dark:border-dark-border">
                <button
                    onClick={() => navigate(-1)}
                    className="p-2 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                >
                    <ArrowLeft size={24} className="text-primary dark:text-golden" />
                </button>
                <h1 className="text-xl font-bold text-light-text dark:text-dark-text">
                    New Billing Schedule
                </h1>
            </div>

            <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border space-y-8">
                {/* Basic Info */}
                <div className="space-y-4">
                    <h2 className="text-lg font-bold text-primary border-b border-light-border dark:border-dark-border pb-2 flex items-center gap-2">
                        <Receipt size={20} /> Schedule Basics
                    </h2>
                    <div>
                        <label className={labelClass}>Schedule Name</label>
                        <p className="text-xs text-light-subtle mb-2">Give this schedule a descriptive name.</p>
                        <input
                            type="text"
                            value={scheduleName}
                            onChange={(e) => setScheduleName(e.target.value)}
                            placeholder="e.g., Monthly Rent Cycle"
                            className={inputClass}
                        />
                    </div>
                </div>

                {/* Properties */}
                <div className="space-y-4">
                    <h2 className="text-lg font-bold text-primary border-b border-light-border dark:border-dark-border pb-2 flex items-center gap-2">
                        <Building2 size={20} /> Properties to Include
                    </h2>
                    <p className="text-xs text-light-subtle">Select all properties this schedule applies to.</p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-60 overflow-y-auto p-1">
                        {allProperties.map(property => {
                            const isSelected = selectedPropertyIds.includes(property.id);
                            return (
                                <button
                                    key={property.id}
                                    onClick={() => togglePropertySelection(property.id)}
                                    className={`flex items-center gap-3 p-4 rounded-xl border text-left transition-all ${
                                        isSelected 
                                            ? "border-primary bg-primary/10 text-primary" 
                                            : "border-light-border dark:border-dark-border bg-light-input dark:bg-dark-input hover:border-primary/50"
                                    }`}
                                >
                                    <div className={`w-5 h-5 rounded-md flex items-center justify-center border transition-colors ${
                                        isSelected ? "bg-primary border-primary" : "border-light-subtle dark:border-dark-subtle bg-white dark:bg-dark-bg"
                                    }`}>
                                        {isSelected && <X size={14} className="text-white rotate-45 transform" />}
                                    </div>
                                    <span className={`font-medium ${isSelected ? 'text-primary' : 'text-light-text dark:text-dark-text'}`}>
                                        {property.name}
                                    </span>
                                </button>
                            );
                        })}
                        {allProperties.length === 0 && (
                            <p className="text-sm text-red-500 col-span-2">No properties found. Please create a property first.</p>
                        )}
                    </div>
                </div>

                {/* Timing */}
                <div className="space-y-4">
                    <h2 className="text-lg font-bold text-primary border-b border-light-border dark:border-dark-border pb-2 flex items-center gap-2">
                        <Calendar size={20} /> Schedule Timing
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                            <label className={labelClass}>Generation Day</label>
                            <p className="text-xs text-light-subtle mb-2">Day of month to create invoices (1-31)</p>
                            <input
                                type="number"
                                min="1" max="31"
                                value={generationDay}
                                onChange={(e) => setGenerationDay(e.target.value)}
                                placeholder="e.g., 25"
                                className={inputClass}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Due Day Offset</label>
                            <p className="text-xs text-light-subtle mb-2">Days after generation when invoice is due</p>
                            <input
                                type="number"
                                min="0" max="31"
                                value={dueDay}
                                onChange={(e) => setDueDay(e.target.value)}
                                placeholder="e.g., 5"
                                className={inputClass}
                            />
                        </div>
                    </div>
                </div>

                {/* Line Items */}
                <div className="space-y-4">
                    <h2 className="text-lg font-bold text-primary border-b border-light-border dark:border-dark-border pb-2">
                        Recurring Line Items
                    </h2>
                    
                    <div className="flex justify-between items-center bg-primary/5 border border-primary/20 p-4 rounded-lg mb-6 shadow-sm">
                        <span className="font-medium text-light-text dark:text-dark-text">Monthly Rent</span>
                        <span className="text-sm text-primary font-medium italic">Uses each unit's default rent</span>
                    </div>

                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <label className={labelClass}>Additional Fixed Charges</label>
                            <Info size={16} className="text-light-subtle dark:text-dark-subtle" />
                        </div>
                        <p className="text-xs text-light-subtle mb-4">Add other fixed charges like Waste or Security that apply to ALL tenants in this schedule.</p>
                        
                        <div className="space-y-3">
                            {fixedCharges.map((item, index) => (
                                <div key={item.id} className="flex gap-3 items-center">
                                    <input
                                        type="text"
                                        placeholder="Charge Description"
                                        value={item.description}
                                        onChange={(e) => handleChargeChange(item.id, "description", e.target.value)}
                                        className={`${inputClass} flex-1`}
                                    />
                                    <input
                                        type="number"
                                        placeholder="Amount"
                                        value={item.amount}
                                        onChange={(e) => handleChargeChange(item.id, "amount", e.target.value)}
                                        className={`${inputClass} w-32`}
                                    />
                                    <button 
                                        onClick={() => removeCharge(item.id)}
                                        className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                    >
                                        <X size={24} />
                                    </button>
                                </div>
                            ))}
                        </div>

                        <button
                            onClick={addCharge}
                            className="flex items-center gap-2 px-4 py-2 mt-4 bg-primary/10 text-primary rounded-lg font-medium hover:bg-primary/20 transition-colors"
                        >
                            <Plus size={18} /> Add Fixed Charge
                        </button>
                    </div>
                </div>

                <div className="pt-4">
                    <button
                        onClick={handleCreateSchedule}
                        disabled={isSubmitting || selectedPropertyIds.length === 0}
                        className="w-full flex items-center justify-center gap-2 py-4 bg-primary text-white rounded-xl font-bold text-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
                    >
                        {isSubmitting ? (
                            <><Loader2 className="animate-spin" size={20} /> Generating...</>
                        ) : (
                            <><Save size={20} /> Create Schedule</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
